import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { IWorkOrderByCollectionId, IWorkOrderEvidenceByCollectionId } from "../../../../common/data/dtos/work-order-by-collection-id";
import Card, { CardBody, CardHeader, CardLabel, CardTitle } from "../../../../components/bootstrap/Card";
import { MoreActions } from "../../../../components/extras/MoreActions";
import { showWarning } from "../../../../components/extras/Notifications";
import { aggregateExecutedServices } from "../../../../helpers/executedServicesHelper";
import { aggregateEvidences, calculatePhotoRanges } from "../../../../helpers/photoRangeHelper";
import fileService from "../../../../services/file-service";
import workOrderService from "../../../../services/work-order-service";
import LoadingPage from "../../loading/LoadingPage";
import { WorkOrderDetailModal } from "./WorkOrderDetailModal";


export const CommonWorkOrderView = () => {

    const [searchParams] = useSearchParams();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isModalOpened, setIsModalOpened] = useState(false);

    const [workOrders, setWorkOrders] = useState<IWorkOrderByCollectionId[]>([]);
    const [beforeEvidence, setBeforeEvidence] = useState<string>();
    const [afterEvidence, setAfterEvidence] = useState<string>();
    const [canDisplayServices, setCanDisplayServices] = useState<boolean>(false);
    const [totalAmount, setTotalAmount] = useState<number>();

    const aggregatedEvidences = useMemo(() => aggregateEvidences(workOrders), [workOrders]);
    const photoRanges = useMemo(() => calculatePhotoRanges(workOrders), [workOrders]);
    const executedServices = useMemo(() => aggregateExecutedServices(workOrders), [workOrders]);

    useEffect(() => {
        fetchData();
    }, []);


    const fetchData = async () => {
        const idsParam = searchParams.get('ids'); // Pega a string única
        const workOrderIds = idsParam ? idsParam.split(',') : [];

        if (!workOrderIds) {
            showWarning('Ordens de serviço não encontradas');
            return;
        }

        setIsLoading(true);

        try {

            setIsLoading(true);

            const response = await workOrderService.getWorkOrderGetByCollectionId(workOrderIds);

            setIsLoading(false);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            setWorkOrders(response.workOrders || []);

            console.log(response.workOrders);

            if (response.workOrders && response.workOrders.length > 0) {
                const canDisplayServices = response?.workOrders[0].canDisplayServices;
                setCanDisplayServices(canDisplayServices);

                if (canDisplayServices) {

                    const grandTotalAmount = response.workOrders
                        .flatMap((wo) => wo.workOrderServices || [])
                        .reduce((sum, s) => sum + (s.totalServiceAmount || 0), 0);

                    setTotalAmount(grandTotalAmount);
                }
            }
        } catch (error) {
            showWarning('Erro ao carregar dados');
        } finally {
            setIsLoading(false);
        }
    }

    if (isLoading) {
        return <LoadingPage />;
    }

    const fetchEvidence = async (selectedWorkOrderEvidence: IWorkOrderEvidenceByCollectionId) => {

        try {
            if (!selectedWorkOrderEvidence) {
                showWarning('Evidências não encontradas');
                return;
            }

            setAfterEvidence('');
            setBeforeEvidence('');

            const beforeKey = getKey(selectedWorkOrderEvidence?.beforeShot);
            const afterkey = getKey(selectedWorkOrderEvidence?.afterShot);

            if (!beforeKey && !afterkey) {
                showWarning('Chaves das evidências não encontradas');
                return;
            }

            setIsLoading(true);

            const promises = [
                beforeKey ? fileService.getEvidenceImageUrl(beforeKey) : Promise.resolve({ url: '', error: null }),
                afterkey ? fileService.getEvidenceImageUrl(afterkey) : Promise.resolve({ url: '', error: null })
            ];

            const [beforeResponse, afterResponse] = await Promise.all(promises);

            //setIsSubmitting(false);

            if (beforeResponse.error) {
                showWarning(beforeResponse.error);
                return;
            }

            if (afterResponse.error) {
                showWarning(afterResponse.error);
                return;
            }

            setBeforeEvidence(beforeResponse.url || '');
            setAfterEvidence(afterResponse.url || '');

            toggleModal(true);

        } catch {
            showWarning('Falha ao recuperar evidências')
        } finally {
            setIsLoading(false);
        }
    }

    const getKey = (url?: string) => {

        if (!url) {
            return;
        }

        const parsedUrl = new URL(url);
        const path = parsedUrl.pathname;
        const key = path.substring(path.lastIndexOf('/') + 1);

        return key;
    }

    const toggleModal = (isVisible: boolean) => {
        setIsModalOpened(isVisible);
    };

    const onModalClose = () => {
        toggleModal(false);
    };

    const onAfterSubmit = async () => {
        onModalClose();
    };


    return (
        <>
            <Card>
                <CardHeader borderSize={1}>
                    <CardLabel icon='InsertDriveFile' iconColor='primary'>
                        <CardTitle tag='div' className='h5'>
                            Ordens de Serviço
                        </CardTitle>
                    </CardLabel>
                </CardHeader>

                <CardBody>
                    <>
                        {workOrders.length > 0 && (
                            <>
                                <table className='table table-bordered'>
                                    <tbody>
                                        <tr>
                                            <td className='text-center'><h4><b>{workOrders[0].customerName}</b></h4></td>
                                        </tr>
                                        <tr>
                                            <td className='text-center'><h4><b>{workOrders[0].serviceTypeDescription}</b></h4></td>
                                        </tr>
                                    </tbody>
                                </table>

                                <table className='table table-bordered'>
                                    <tbody>
                                        <tr>
                                            <td className='text-center'><h5><b>{'EMPRESA CONTRATADA'}</b></h5></td>
                                            <td><h5>C & F EMPREENDIMENTOS ELETRICOS TELEFONICOS E SERVIÇOS LTDA</h5></td>
                                        </tr>
                                        <tr>
                                            <td className='text-center'><h5><b>{'RESPONSÁVEL'}</b></h5></td>
                                            <td><h5>{workOrders[0].employeeName}</h5></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </>
                        )}

                        {
                            canDisplayServices ? (
                                <>
                                    <table className='table table-responsive table-bordered'>
                                        <thead className='table-light'>
                                            <tr>
                                                <th rowSpan={2} className='text-center'>DATA</th>
                                                <th rowSpan={2} className='text-center'>TORRE</th>
                                                <th rowSpan={2} className='text-center'>RAMAL</th>
                                                <th rowSpan={2} className='text-center'>FOTOS</th>
                                                {
                                                    executedServices && executedServices.map((es, index) => (
                                                        // O Fragment com 'key' resolve o erro de múltiplos elementos irmãos
                                                        <React.Fragment key={es.id || index}>
                                                            <th className='text-center'>{es.description} {es.unit}</th>
                                                        </React.Fragment>
                                                    ))
                                                }
                                            </tr>
                                            <tr>
                                                {
                                                    executedServices && executedServices.map((es, index) => (
                                                        <React.Fragment key={es.id || index}>
                                                            <th className='text-center'>R$ {es.price}</th>
                                                        </React.Fragment>
                                                    ))
                                                }

                                            </tr>


                                        </thead>
                                        <tbody>
                                            {workOrders.map((wo, index) => (
                                                <tr key={index}>
                                                    <td className='text-center'>{dayjs(wo.segmentStartDate).format('DD/MM/YYYY')}</td>
                                                    <td className='text-center'>{wo.tower}</td>
                                                    <td className='text-center'>{wo.lineName}</td>
                                                    <td className='text-center'>{photoRanges[index]}</td>

                                                    {executedServices.map((executedService) => {
                                                        const totalPerformed = wo.workOrderServices
                                                            .filter(
                                                                (s) =>
                                                                    s.serviceId === executedService.serviceId &&
                                                                    s.price === executedService.price
                                                            )
                                                            .reduce((sum, s) => sum + (s.totalPerformed || 0), 0);

                                                        // 3. Renderiza a célula aplicando a formatação (Equivalente ao .Text e ToString)
                                                        return (
                                                            <td key={`${executedService.serviceId}-${executedService.price}`} className="text-center">
                                                                {totalPerformed > 0
                                                                    ? totalPerformed.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                                    : "-"}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="table-light fw-bold">
                                            <tr>
                                                <td colSpan={4} className="text-end align-middle pe-3 font-weight-bold">
                                                    TOTAL (R$)
                                                </td>

                                                {executedServices.map((executedService) => {

                                                    const totalAmount = workOrders
                                                        .flatMap((wo) => wo.workOrderServices || [])
                                                        .filter(
                                                            (s) =>
                                                                s.serviceId === executedService.serviceId &&
                                                                s.price === executedService.price
                                                        )
                                                        .reduce((sum, s) => sum + (s.totalServiceAmount || 0), 0);

                                                    return (
                                                        <td
                                                            key={`total-${executedService.serviceId}-${executedService.price}`}
                                                            className="text-center align-middle font-weight-bold"
                                                        >
                                                            {totalAmount.toLocaleString('pt-BR', {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2
                                                            })}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        </tfoot>
                                    </table>

                                    <table className='table table-bordered'>
                                        <tbody>
                                            <tr>
                                                <td className='text-end'><h5><b>VALOR TOTAL: {totalAmount?.toLocaleString('pt-BR', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}</b></h5></td>
                                            </tr>
                                        </tbody>
                                    </table>

                                </>
                            ) : (
                                <table className='table table-responsive table-bordered'>
                                    <thead className='table-light'>
                                        <tr>
                                            <th className='text-center'>DATA</th>
                                            <th className='text-center'>FOTOS</th>
                                            <th className='text-center'>ANOTAÇÕES</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {workOrders.map((wo, index) => (
                                            <tr key={index}>
                                                <td className='text-center'>{dayjs(wo.segmentStartDate).format('DD/MM/YYYY')}</td>
                                                <td className='text-center'>{photoRanges[index]}</td>
                                                <td className='text-center'>{wo.notes}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )
                        }


                        <table className='table table-bordered'>
                            <thead className='table-light'>
                                <tr>
                                    <th className='text-center'>Data Evidência Antes</th>
                                    <th className='text-center'>Localização Antes</th>
                                    <th className='text-center'>Data Evidência Depois</th>
                                    <th className='text-center'>Localização Depois</th>
                                    <td aria-labelledby='Actions' />
                                </tr>
                            </thead>
                            <tbody>
                                {aggregatedEvidences.map((item: IWorkOrderEvidenceByCollectionId) => (
                                    <tr key={item.id}>
                                        <td>{item.beforeShotDate ? dayjs(item.beforeShotDate).format('DD/MM/YYYY') : '-'}</td>
                                        <td>{item?.beforeShotLocation ?? '-'}</td>
                                        <td>{item.afterShotDate ? dayjs(item.afterShotDate).format('DD/MM/YYYY') : '-'}</td>
                                        <td>{item?.afterShotLocation ?? '-'}</td>


                                        <MoreActions
                                            id={item.id}
                                            showEdit={false}
                                            showDelete={false}
                                            customActions={[
                                                {
                                                    label: 'Visualizar',
                                                    icon: 'RemoveRedEye',
                                                    onClickAction: () => fetchEvidence(item),
                                                }
                                            ]}
                                        />
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                </CardBody>
            </Card>

            <WorkOrderDetailModal
                isModalOpened={isModalOpened}
                onModalClose={onModalClose}
                onAfterSubmit={onAfterSubmit}
                beforeEvidence={beforeEvidence}
                afterEvidence={afterEvidence}
            />


        </>
    );

}